<template>
  <div class="blogposts-page">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>{{ $t('menu.translations.subpages.blogPosts.header') }}</h1>
        <p class="page-subtitle">{{ $t('menu.translations.subpages.blogPosts.subtitle', { count: user.blogPosts.length }) }}</p>
      </div>
      <button class="appButton" @click="$router.push('/translations')">
        <i class="bx bx-arrow-back" style="margin-right: 0.25rem;"></i>
        {{ $t('menu.settings.back') }}
      </button>
    </div>

    <div class="bp-layout">
      <!-- Left Column: Post List -->
      <div class="bp-sidebar">
        <h3>{{ $t('menu.translations.subpages.blogPosts.sidebarTitle') }}</h3>
        <ul class="bp-list">
          <li v-for="post in user.blogPosts" :key="post.idNumber"
            class="bp-item"
            :class="{ 'bp-item--active': post.idNumber === user.selectedPostId }"
            @click="user.mergeDatabaseTranslations(post.id, post.idNumber, 'Article')">
            {{ post['blog-title'] }}
          </li>
        </ul>
      </div>

      <!-- Right Column: Translation Cards -->
      <div class="bp-content">

        <!-- Default Language (English) -->
        <div class="lang-card lang-card--primary">
          <div class="lang-card-header">
            <span class="lang-badge lang-badge--primary">{{ $t('common.defaultLanguage') }}</span>
          </div>
          <div class="lang-card-body">
            <div class="lang-fields-row">
              <div class="lang-field-group">
                <div class="field-with-toggle">
                  <label>{{ $t('common.title') }}</label>
                  <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('en', 'title')" @change="setIncludeValue('en', 'title', $event.target.checked)" />
                </div>
                <SemanticHTMLEditor v-model="user.translations.en.title" userMarket="en" userModel="title" />

                <div class="field-with-toggle mt-4">
                  <label>{{ $t('common.bodyHtml') }}</label>
                  <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('en', 'body_html')" @change="setIncludeValue('en', 'body_html', $event.target.checked)" />
                </div>
                <SemanticHTMLEditor v-model="user.translations.en.body_html" userMarket="en" userModel="body_html" />

                <div class="field-with-toggle mt-4">
                  <label>{{ $t('common.summaryHtml') }}</label>
                  <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('en', 'summary_html')" @change="setIncludeValue('en', 'summary_html', $event.target.checked)" />
                </div>
                <SemanticHTMLEditor v-model="user.translations.en.summary_html" userMarket="en" userModel="summary_html" />

                <div class="lang-actions">
                  <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                  <button v-if="user.userRoleId < 2" class="appButton" @click="updateShopify('en', user.translations.en)">{{ $t('menu.settings.updateShopify') }} (ENG)</button>
                </div>
              </div>
              <div class="lang-field-group">
                <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.en.meta_description) }}</span></label>
                <SemanticHTMLEditor v-model="user.translations.en.meta_description" userMarket="en" userModel="meta_description" />
              </div>
            </div>
          </div>
        </div>

        <!-- Language Cards -->
        <div class="lang-stack">

          <!-- German -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/de.png" alt="German Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.german') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('de', 'title')" @change="setIncludeValue('de', 'title', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.de.title" userMarket="de" userModel="title" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('de', 'body_html')" @change="setIncludeValue('de', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.de.body_html" userMarket="de" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('de', 'summary_html')" @change="setIncludeValue('de', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.de.summary_html" userMarket="de" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.de.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.de.meta_description" userMarket="de" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('de')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('de', user.translations.de)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('de', user.translations.de)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Spanish -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/es.png" alt="Spanish Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.spanish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('es', 'title')" @change="setIncludeValue('es', 'title', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.es.title" userMarket="es" userModel="title" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('es', 'body_html')" @change="setIncludeValue('es', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.es.body_html" userMarket="es" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('es', 'summary_html')" @change="setIncludeValue('es', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.es.summary_html" userMarket="es" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.es.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.es.meta_description" userMarket="es" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('es')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('es', user.translations.es)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('es', user.translations.es)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Danish -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/da.png" alt="Danish Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.danish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('da', 'title')" @change="setIncludeValue('da', 'title', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.da.title" userMarket="da" userModel="title" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('da', 'body_html')" @change="setIncludeValue('da', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.da.body_html" userMarket="da" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('da', 'summary_html')" @change="setIncludeValue('da', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.da.summary_html" userMarket="da" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.da.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.da.meta_description" userMarket="da" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('da')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('da', user.translations.da)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('da', user.translations.da)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Dutch -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/nl.png" alt="Dutch Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.dutch') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('nl', 'title')" @change="setIncludeValue('nl', 'title', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.nl.title" userMarket="nl" userModel="title" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('nl', 'body_html')" @change="setIncludeValue('nl', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.nl.body_html" userMarket="nl" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('nl', 'summary_html')" @change="setIncludeValue('nl', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.nl.summary_html" userMarket="nl" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.nl.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.nl.meta_description" userMarket="nl" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('nl')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('nl', user.translations.nl)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('nl', user.translations.nl)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- French -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/fr.png" alt="French Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.french') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('fr', 'title')" @change="setIncludeValue('fr', 'title', $event.target.checked)" />
                  </div>
                  <input v-model="user.translations.fr.title" type="text" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('fr', 'body_html')" @change="setIncludeValue('fr', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.fr.body_html" userMarket="fr" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('fr', 'summary_html')" @change="setIncludeValue('fr', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.fr.summary_html" userMarket="fr" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.fr.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.fr.meta_description" userMarket="fr" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('fr')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('fr', user.translations.fr)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('fr', user.translations.fr)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Swedish -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/sv.png" alt="Swedish Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.swedish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('sv', 'title')" @change="setIncludeValue('sv', 'title', $event.target.checked)" />
                  </div>
                  <input v-model="user.translations.sv.title" type="text" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('sv', 'body_html')" @change="setIncludeValue('sv', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.sv.body_html" userMarket="sv" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('sv', 'summary_html')" @change="setIncludeValue('sv', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.sv.summary_html" userMarket="sv" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.sv.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.sv.meta_description" userMarket="sv" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('sv')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('sv', user.translations.sv)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('sv', user.translations.sv)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- UK -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/uk.png" alt="UK Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.britishEnglish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <div class="field-with-toggle">
                    <label>{{ $t('common.title') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('gb', 'title')" @change="setIncludeValue('gb', 'title', $event.target.checked)" />
                  </div>
                  <input v-model="user.translations.gb.title" type="text" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.bodyHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('gb', 'body_html')" @change="setIncludeValue('gb', 'body_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.gb.body_html" userMarket="gb" userModel="body_html" />
                  <div class="field-with-toggle mt-4">
                    <label>{{ $t('common.summaryHtml') }}</label>
                    <input type="checkbox" class="field-checkbox" :checked="findIncludeValue('gb', 'summary_html')" @change="setIncludeValue('gb', 'summary_html', $event.target.checked)" />
                  </div>
                  <SemanticHTMLEditor v-model="user.translations.gb.summary_html" userMarket="gb" userModel="summary_html" />
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.gb.meta_description) }}</span></label>
                  <SemanticHTMLEditor v-model="user.translations.gb.meta_description" userMarket="gb" userModel="meta_description" />
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('gb')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('gb', user.translations.gb)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('gb', user.translations.gb)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSettings } from '@/store/user'
import mixins from '@/components/mixins';

export default {
  mixins: [mixins],
  setup() {
    const user = useSettings();
    user.getBlogPosts();

    return { user }
  },
  data() {
    return {
      collectionHandleData: '',
      digests: [],
      defaultProperties: {
        en: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        de: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        es: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        sv: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        fr: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        nl: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        da: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
        gb: {
          include: [{key: 'meta_title', value: true},{key: 'meta_description', value: true}],
        },
      }
    }
  },
  methods: {
    async tooglePropertyInclusion(lang, property) {
      const index = this.user.translations.properties[lang].include.findIndex(p => p.key === property);
      const val = this.user.translations.properties[lang].include[index]?.value;

      if(!index || index === -1) {
        this.user.translations.properties[lang].include.push({key: property, value: true});
      }

      if (val === false) {
        this.user.translations.properties[lang].include[index].value = true;
      } else {
        this.user.translations.properties[lang].include[index].value = false;
      }
    },
    async updateLocale(locale) {
      const fields = ['title', 'body_html', 'summary_html', 'meta_title', 'meta_description'];

      for (const lang in this.user.translations) {
        if (lang === 'en') continue;
        if (lang !== locale) continue;

        for (const field of fields) {
          if (this.user.translations[lang][field] === '') continue;

          const res = await this.user.getTranslations({
            locale: locale,
            text: this.user.translations[locale][field],
          });

          this.user.translations[locale][field] = res.translation;
        }
      }
    },
    async updateShopify(locale, langObj) {
      console.log(`Updating Shopify for locale: ${locale}`, langObj);

      const fields = this.user.translations.properties[locale].include;
      const resourceId = this.user.selectedCollectionId;

      for (const field of fields) {
        console.log(`Processing field: ${field.key} for locale: ${locale}`);
        if (langObj[field] === '') continue;

        let key = '';
        let type = '';
        let mutation = '';
        let title = null;

        const escapedValue = await this.escapeGraphQLString(langObj[field.key]);

        if(locale === 'en') {
          if (field.key === 'title') key = 'title', type = 'single_line_text_field';
          if (field.key === 'body_html') key = 'body_html',  type = 'multi_line_text_field';
          if (field.key === 'summary_html') key = 'summary_html',  type = 'multi_line_text_field';
          if (field.key === 'meta_description') key = 'description_tag', type = 'single_line_text_field';

          mutation = `
            mutation {
              collectionUpdate(
                input: {
                  id: "${resourceId}",
                  metafields: [
                    {
                      namespace: "global",
                      key: "${key}",
                      type: "${type}",
                      value: "${escapedValue}"
                    }
                  ]
                }
              ) {
                collection {
                  id
                }
                userErrors {
                  field
                  message
                }
              }
            }
          `;

        } else {
          if (field.key === 'title') key = 'title', type = 'single_line_text_field';
          if (field.key === 'body_html') key = 'body_html', type = 'multi_line_text_field';
          if (field.key === 'summary_html') key = 'summary_html', type = 'multi_line_text_field';
          if (field.key === 'meta_description') key = 'meta_description', type = 'single_line_text_field';

          console.log('Finding digest for key:', key);

          const digest = this.user.translations.digests.find(d => d.key === key)?.value || '';

          mutation = `
            mutation {
              translationsRegister(
                resourceId: "${resourceId}",
                translations: [
                  {
                    locale: "${locale}",
                    key: "${key}",
                    value: "${escapedValue}",
                    translatableContentDigest: "${digest}"
                  }
                ]
              ) {
                userErrors {
                  field
                  message
                }
              }
            }
          `;
        }

        try {
          const res = await this.user.saveTranslations(mutation);

        } catch (error) {
          console.error('Fejl ved opdatering:', error.response?.data || error.message);
        }
      }
    },
    async escapeGraphQLString(str) {
      if (!str) return str;
      return str
        .replace(/<p><\/p>/g, '<br/>')
        .replace(/&nbsp;/g, ' ')
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '')
        .replace(/\\/g, '\\\\')
        .replace(/"""/g, '\\"""')
        .replace(/"/g, '\\"');
    },
    validateTitleLength(field) {
      if (!field) return '0';
      if (field.length > 70) {
        return `Too long by ${field.length - 70} characters`;
      } else {
        return `${field.length}/70 characters`;
      }
    },
    validateDescriptionLength(field) {
      if (!field) return '0';
      if (field.length > 160) {
        return `Too long by ${field.length - 160} characters`;
      } else {
        return `${field.length}/160 characters`;
      }
    },
    findIncludeValue(lang, key) {
      const translation = this.user.translations.properties[lang];
      if (!translation) return false;

      const item = translation.include.find(i => i.key === key);
      return item ? item.value : false;
    },
    setIncludeValue(lang, key, newValue) {
      console.log(`Setting include value for lang: ${lang}, key: ${key}, newValue: ${newValue}`);
      const translation = this.user.translations.properties[lang];
      if (!translation) return;

      const item = translation.include.find(i => i.key === key);
      if (item) {
        item.value = newValue;
      } else {
        translation.include.push({ key, value: newValue });
      }
    }
  },
}
</script>

<style scoped>
/* Layout */
.blogposts-page {
  animation: fadeIn 0.3s ease-out;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}
.page-subtitle {
  color: #9aa0a8;
  font-size: 0.875rem;
  margin-top: 0.125rem;
}

.bp-layout {
  display: flex;
  gap: 1.5rem;
  min-height: calc(100vh - 180px);
}

/* Sidebar */
.bp-sidebar {
  width: 240px;
  flex-shrink: 0;
  background: #1a2028;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem;
  overflow-y: auto;
}
.bp-sidebar h3 {
  margin-bottom: 0.75rem;
}
.bp-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.bp-item {
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #9aa0a8;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bp-item:hover {
  background: rgba(255, 255, 255, 0.04);
  color: #e8eaed;
}
.bp-item--active {
  background: rgba(117, 35, 35, 0.1);
  color: #e8eaed;
  border-left: 3px solid #a63c3c;
  padding-left: calc(0.75rem - 3px);
}

/* Content area */
.bp-content {
  flex: 1;
  min-width: 0;
}

/* Language cards */
.lang-card {
  background: #1a2028;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.15);
}
.lang-card--primary {
  border-color: rgba(117, 35, 35, 0.2);
}
.lang-card--primary::before {
  content: '';
  display: block;
  height: 2px;
  background: linear-gradient(90deg, #752323, #a63c3c, #c94a4a);
}

.lang-card-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.lang-flag {
  border-radius: 3px;
}
.lang-name {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  color: #e8eaed;
}
.lang-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
}
.lang-badge--primary {
  background: rgba(117, 35, 35, 0.15);
  color: #c94a4a;
  border: 1px solid rgba(117, 35, 35, 0.2);
}

.lang-card-body {
  padding: 1.25rem;
}

.lang-fields-row {
  display: flex;
  gap: 1.25rem;
}
.lang-field-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.lang-field-group label {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}
.lang-field-group input[type="text"] {
  width: 100%;
  padding: 0.5rem 0.75rem;
}
.lang-field-group textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  resize: vertical;
}

/* Field with checkbox toggle */
.field-with-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}
.field-with-toggle label {
  margin-bottom: 0;
}
.field-checkbox {
  width: 16px !important;
  height: 16px;
  border-radius: 4px !important;
  accent-color: #a63c3c;
  cursor: pointer;
  flex-shrink: 0;
}

.mt-4 {
  margin-top: 1rem;
}

.seo-counter {
  font-size: 0.625rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #5c6370;
}

.lang-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

/* Full-width stack for blog posts (richer content) */
.lang-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
